import React, { MouseEventHandler, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MutationFunctionOptions, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import StockTakeReportMutation from '../../graphql/mutations/product/StockTakeReportMutation.graphql';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../connectedForm/Button';
import StockTakeCommitMutation from '../../graphql/mutations/product/StockTakeCommitMutation.graphql';
import { alertAction, handleComponentQueryError } from '../../utils/parseErrorResponse';
import { serviceActions } from '../../state/modules/service';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import * as entities from '../../graphql/entities';
import { Product } from '../../graphql/entities';
import { IJobMeta, useBackgroundMutation, useBackgroundProcess } from '../../utils/BackgroundJobs';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useRefFunction } from '../../utils/hooks';
import getClientMutationId from '../../utils/getClientMutationId';
import {
  AlertType,
  FilterOperation,
  IStockTake,
  IStockTakeActivationToggleMutationResult,
  IStockTakeActivationToggleMutationVariables,
  IStockTakeCommitMutationResult,
  IStockTakeCommitMutationVariables,
  IStockTakeInput,
  IStockTakeReportMutationResult,
  IStockTakeReportMutationVariables,
  IStockTakesUpdateMutationResult,
  IStockTakesUpdateMutationVariables,
  Maybe,
  ReportType,
  StockTakeType,
} from '../../graphql/schema/p4types';
import { BasicConfirmation } from '../modals/ConfirmationPrompt';
import HeaderTitle from '../connectedForm/HeaderTitle';
import HeaderAction from '../connectedForm/HeaderAction';
import ActionButtons from '../connectedForm/ActionButtons';
import { IFormAction } from '../../@types/entityTypes';
import { IStockTakeFormData } from '../../views/product/ProductsStockTakeForm';
import { PrimaryTheme, SuccessTheme } from '../../styles/themes/default/defaultFluentTheme';
import BreadCrumbs from '../connectedForm/BreadCrumbs';
import { useHistory } from 'react-router';
import StockTakeLine from '../../graphql/entities/product/StockTakeLine';
import { ContextualMenuItemType } from '@fluentui/react';
import { usePostConfirmationPrompt } from '../../utils/SalesLedgerHooks';
import { FetchResult } from '@apollo/client/link/core/types';
import StockTakeActivationToggle from '../../graphql/mutations/product/StockTakeActivationToggleMutation.graphql';
import HeaderStatus, { StatusStyle } from '../connectedForm/HeaderStatus';
import { IEditableDataGridAction, IEditableDataGridViewController } from '../view/UseEditableDataGridViewController';
import { PostSelectButton, SaveSelectButton } from '../SelectButton';
import { SubmitOption } from '../../@types/connectedFormTypes';
import { enumToTitleCase } from '../../utils/TitleCase';
import StockTake from '../../graphql/entities/product/StockTake';
import DocumentModal2 from '../modals/DocumentModal2';
import { onDocumentComplete } from '../../utils/DocumentUtils';
import { usePerformSave } from '../connectedForm/Hooks/usePerformSave';
import PostStockTakeModal from '../modals/PostStockTakeModal';
import { useProductConfig } from '../../utils/FetchConfig';

export enum StockTakeAction {
  Post = 'STOCK_TAKE_POST',
  Save = 'STOCK_TAKE_SAVE',
  AddProducts = 'STOCK_TAKE_ADD_PRODUCTS',
  AddNewLocation = 'STOCK_TAKE_ADD_NEW_LOCATION',

  /**
   * @deprecated - Used for (Legacy) Stock Take Progressive & Stock Take Direct.
   */
  AddLocations = 'STOCK_TAKE_ADD_LOCATIONS',
  ImportLines = 'IMPORT_LINES',
  Clear = 'STOCK_TAKE_CLEAR',
  Delete = 'STOCK_TAKE_DELETE',
  Report = 'STOCK_TAKE_REPORT',
  Variance = 'VARIANCE',
  ActivateToggle = 'STOCK_TAKE_ACTIVATION_TOGGLE',
  ChangeStatus = 'CHANGE_STATUS',
}

export const StockTakeReportModal = ({
  modalOpen,
  onClose,
  stockTakeId,
  stockTakeLineCount,
}: {
  modalOpen: boolean;
  onClose: () => void;
  stockTakeId: number;
  stockTakeLineCount: number;
}) => {
  const emailAddress = useSelector((store: any) => store.app?.user?.email);
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const [runReport] = useBackgroundMutation<IStockTakeReportMutationResult, IStockTakeReportMutationVariables>(
    StockTakeReportMutation,
    {
      onCompleteJob: (job) => {
        onDocumentComplete?.({ documentName: 'Stock Take Report', dispatch, job });
      },
    }
  );

  const stockTakeReportEntity = useMemo(() => {
    return {
      ...entities.StockTakeLine,
      configuration: {
        title: 'Stock Take Report',
        reportType: ReportType.StockTakeReport,
        defaultConfig: {
          emailList: [{ id: emailAddress, email: emailAddress }],
        },
      },
    };
  }, [emailAddress]);

  return (
    <DocumentModal2
      modalOpen={modalOpen}
      entity={stockTakeReportEntity}
      title={stockTakeReportEntity.configuration.title}
      modalWrapper={true}
      count={stockTakeLineCount}
      blockNavigation={true}
      onClose={onClose}
      allowEmailTo={true}
      allowSubject={false}
      onPrintClick={({ reportParameters }) => {
        runReport({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              reportParameters: normalizeVariables(reportParameters),
              actionCriteria: {
                filters: [
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'stockTake.id',
                      value: stockTakeId,
                    },
                  },
                ],
                actionEntities: [],
                include: false,
              },
            },
          },
        });
      }}
    />
  );
};

type StockTakeHeaderProps = {
  stockTakeType: StockTakeType;
  handleAdd: () => void;
  handleNewLocation: () => void;
  handleClear: (data: IStockTakeFormData, close: () => void) => void;
  onReject: (data: IStockTakeFormData, close: () => void) => void;
  onCancel: MouseEventHandler<any>;
  handleSubmit: MouseEventHandler<any>;
  formUpdate: (
    options?: MutationFunctionOptions<IStockTakesUpdateMutationResult, IStockTakesUpdateMutationVariables>
  ) => Promise<FetchResult<IStockTakesUpdateMutationResult>>;
  viewController: IEditableDataGridViewController;
};

/**
 * @deprecated - Still used for *DIRECT* Stock Takes.
 *             - Still accessible via link from new Stock Take form for legacy purposes & testing. (Fallback if new one fails).
 *             - For removal in 2026.
 */
const StockTakeHeader = ({
  handleSubmit,
  handleAdd,
  handleNewLocation,
  handleClear,
  onReject,
  formUpdate,
  stockTakeType,
  onCancel,
  viewController: { actions: viewControllerActions },
}: StockTakeHeaderProps) => {
  const connectedForm = useContext(ConnectedFormContext);
  const stockTake = connectedForm.values as IStockTake;
  const history = useHistory();
  const productConfig = useProductConfig();

  const [confirmClearModalOpen, setConfirmClearModalOpen] = useState<boolean>(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);
  const [reportModalOpen, setReportModalOpen] = useState<boolean>(false);
  const [postModalOpen, setPostModalOpen] = useState<boolean>(false);
  const [postButtonDisabled, setPostButtonDisabled] = useState<boolean>(false);

  const [postConfirmationState, setPostConfirmationState] =
    useState<Maybe<{ meta: any; title: string; message: string }>>(null);
  const [variables, setVariables] = useState<IStockTakeCommitMutationVariables>({} as any);

  const postConfirmationPrompt = usePostConfirmationPrompt(
    postConfirmationState,
    setPostConfirmationState,
    variables,
    setVariables,
    'input.stockTakeCommit.stockTake'
  );
  const [stockTakeCommit] = useMutation<IStockTakeCommitMutationResult, IStockTakeCommitMutationVariables>(
    StockTakeCommitMutation,
    {
      onError: (data) => {
        handleComponentQueryError(data, {
          dispatch,
          entity: Product,
          name: 'Posting Failed',
          customAlertHandler: (_: unknown, meta: any, title: string, message: string) => {
            if (meta.confirmationReference) {
              setPostConfirmationState({
                meta,
                title,
                message,
              });
              return;
            }
            //If no custom handling, call the default handling.
            alertAction({ dispatch, entity: connectedForm?.entity }, meta, title, message);
          },
        });

        setPostButtonDisabled(false);
      },
    }
  );

  useEffect(() => {
    if (!variables.input) return;
    stockTakeCommit({
      variables: normalizeVariables(variables, ['freeStock']),
    }).then((result) => {
      const job = result.data?.stockTakeCommitSave?.result;
      if (job) {
        runBackgroundPostProcess(job as IJobMeta);
      }
    });
    //Disabled eslint exhaustive deps - 'runBackgroundPostProcess' likely to change frequently.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockTakeCommit, variables]);

  const dispatch = useDispatch();
  const runBackgroundPostProcess = useBackgroundProcess({
    title: 'Stock take post',
    onComplete: () => {
      //Can access 'job' from this if needed later.
      dispatch(serviceActions.genericAlert('Committed', 'Stock take posted', AlertType.Success));
      if (connectedForm.submitOptions?.[0]?.value === SubmitOption.Continue) {
        connectedForm.reloadData?.(true);
      } else {
        connectedForm.afterSubmit?.();
      }

      setPostButtonDisabled(false);
    },
    onFailed: (job) => {
      dispatch(
        serviceActions.genericAlert('Generation Unsuccessful', job?.message ? job?.message : '', AlertType.Error)
      );
      setPostButtonDisabled(false);
    },
  });

  const [activationToggle] = useBackgroundMutation<
    IStockTakeActivationToggleMutationResult,
    IStockTakeActivationToggleMutationVariables
  >(StockTakeActivationToggle, {
    jobTitle: 'Toggle Activation',
    onCompleteJob: () => {
      connectedForm.reloadData?.(true);
    },
  });

  const [performSave, saveConfirmationPrompt] = usePerformSave();

  const handlePostConfirm = useCallback(
    (postingDate: Date) => {
      setPostModalOpen(false);
      setPostButtonDisabled(true);

      const setUpPost = (id: number) => {
        const variables = {
          input: {
            clientMutationId: getClientMutationId(),
            stockTakeCommit: {
              stockTakeId: id,
              postingDate: format(postingDate, 'yyyy-MM-dd'),
            },
          },
        };

        const commitVariables: IStockTakeCommitMutationVariables = normalizeVariables(variables);
        setVariables(commitVariables);
      };

      if (connectedForm.dirty) {
        performSave({
          onSaveComplete: ({ result }) => {
            setUpPost(result.id);
          },
        });
      } else {
        setUpPost(connectedForm.values!.id);
      }
    },
    [connectedForm.dirty, connectedForm.values, performSave]
  );

  const postStockTake = useCallback(() => {
    const showPostingDatePicker = productConfig?.stockTakeShowPostingDatePicker;

    // Open the post modal to allow user to select posting date
    if (connectedForm.dirty) {
      performSave({
        onSaveComplete: () => {
          showPostingDatePicker ? setPostModalOpen(true) : handlePostConfirm(new Date());
        },
      });
    } else {
      showPostingDatePicker ? setPostModalOpen(true) : handlePostConfirm(new Date());
    }
  }, [connectedForm.dirty, performSave]);

  const actions = useMemo<Array<IFormAction<StockTakeAction | IEditableDataGridAction>>>(() => {
    const saveAction: IFormAction<StockTakeAction> = {
      key: 'save',
      text: 'Save',
      onClick: handleSubmit,
      actionReference: StockTakeAction.Save,
      individualButton: true,
      individualButtonComponent: SaveSelectButton,
      buttonProps: {
        theme: SuccessTheme,
        saveOptions: [
          {
            value: 'EXIT',
            label: 'Save & exit',
          },
          {
            value: 'CONTINUE',
            label: 'Save & continue',
          },
        ],
      },
      ignorePermissions: true,
    };

    if (!stockTake.id) {
      return [saveAction, ...(viewControllerActions as Array<unknown> as Array<IFormAction<StockTakeAction>>)];
    }

    if (stockTake.id) {
      const persistedStockTakeActions: Array<IFormAction<StockTakeAction>> = [
        {
          key: 'post',
          text: 'Post',
          onClick: postStockTake,
          actionReference: StockTakeAction.Post,
          individualButton: true,
          individualButtonComponent: PostSelectButton,
          buttonProps: {
            theme: PrimaryTheme,
            primary: true,
            disabled: postButtonDisabled,
            saveOptions: [
              {
                value: 'EXIT',
                label: 'Post & exit',
              },
              {
                value: 'CONTINUE',
                label: 'Post & continue',
              },
            ],
          },
          ignorePermissions: true,
          condition: (stockTake) => stockTake.active,
        },
        {
          key: 'addProducts',
          text: 'Add Products',
          onClick: handleAdd,
          actionReference: StockTakeAction.AddProducts,
          individualButton: false,
          ignorePermissions: true,
        },
        {
          key: 'addNewLocation',
          text: 'Add New Location',
          onClick: handleNewLocation,
          actionReference: StockTakeAction.AddNewLocation,
          individualButton: false,
          ignorePermissions: true,
        },
        {
          key: 'importLines',
          text: 'Import Lines',
          onClick: () => {
            history.push({
              pathname: `/Company/Import/Details/New`,
              state: {
                stockTakeId: stockTake.id,
                previousLocation: history.location?.pathname || 'back',
              },
            });
          },
          actionReference: StockTakeAction.ImportLines,
          individualButton: false,
          ignorePermissions: true,
        },
        {
          key: 'clear',
          text: 'Clear',
          actionReference: StockTakeAction.Clear,
          individualButton: false,
          onClick: () => {
            setConfirmClearModalOpen(true);
          },
          ignorePermissions: true,
          condition: (stockTake) => stockTake.stockTakeLines?.length ?? -1 > 0,
        },
        {
          key: 'delete',
          text: 'Delete',
          actionReference: StockTakeAction.Delete,
          individualButton: false,
          onClick: () => {
            setConfirmDeleteModalOpen(true);
          },
          ignorePermissions: true,
          condition: (stockTake) =>
            stockTake.stockTakeLines?.length === 0 && stockTake.stockTakeType !== StockTakeType.Direct,
        },
        {
          key: 'variance',
          text: 'Variance',
          actionReference: StockTakeAction.Variance,
          onClick: () => {
            history.push({
              pathname: `${StockTakeLine.workspaceUrl}${stockTake.id}`,
              state: { previousLocation: history.location?.pathname || 'back' },
            });
          },
          ignorePermissions: true,
          condition: (stockTake): boolean =>
            stockTake.stockTakeType === StockTakeType.Progressive && (stockTake.stockTakeLines?.length ?? -1) > 0,
        },
        {
          key: 'activate',
          text: stockTake.active ? 'Deactivate' : 'Activate',
          onClick: () => {
            activationToggle({
              variables: {
                input: {
                  clientMutationId: getClientMutationId(),
                  stockTakeId: stockTake.id,
                  activate: !stockTake.active,
                },
              },
            });
          },
          actionReference: StockTakeAction.ActivateToggle,
          ignorePermissions: true,
          condition: (stockTake) => !!stockTake.id && stockTake.stockTakeType === StockTakeType.Progressive,
        },
        {
          key: 'actionsReportingHeader',
          actionReference: 'HEADER' as StockTakeAction,
          itemType: ContextualMenuItemType.Header,
          text: 'Reports',
          ignorePermissions: true,
        },
        {
          key: 'report',
          text: 'Report',
          actionReference: StockTakeAction.Report,
          individualButton: false,
          onClick: () => {
            setReportModalOpen(true);
          },
          ignorePermissions: true,
        },
      ];

      return [saveAction, ...persistedStockTakeActions, ...viewControllerActions];
    }
    return [saveAction, ...viewControllerActions];
  }, [
    activationToggle,
    history,
    postButtonDisabled,
    postStockTake,
    handleAdd,
    handleNewLocation,
    handleSubmit,
    stockTake.active,
    stockTake.id,
    viewControllerActions,
  ]);

  const clearStockTake = useRefFunction(() => {
    if (handleClear) {
      handleClear(connectedForm as any, () => null);
    }
    setConfirmClearModalOpen(false);
  });

  const cancelClearStockTake = () => {
    if (onReject) {
      onReject(connectedForm as any, () => null);
    }

    setConfirmClearModalOpen(false);
  };

  const active = stockTake.active || stockTake.stockTakeType === StockTakeType.Direct;

  return (
    <>
      {saveConfirmationPrompt}
      {postConfirmationPrompt}
      <BasicConfirmation
        isOpen={confirmClearModalOpen}
        title={'Stock Take'}
        message={'This will clear down all the stock lines do you wish to continue?'}
        onConfirm={clearStockTake}
        onCancel={cancelClearStockTake}
      />
      <BasicConfirmation
        isOpen={confirmDeleteModalOpen}
        title={`Delete Stock Take ${stockTake.reference ?? ''}`}
        message={'This will remove this Stock Take, do you wish to continue?'}
        onConfirm={() => {
          formUpdate({
            variables: {
              input: {
                clientMutationId: getClientMutationId(),
                stockTake: { ...(normalizeVariables(stockTake) as IStockTakeInput), deleted: true },
              },
            },
          }).then((result) => {
            (connectedForm as any).navigateBack?.(result);
          });
        }}
        onCancel={() => setConfirmDeleteModalOpen(false)}
      />
      <StockTakeReportModal
        modalOpen={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
        }}
        stockTakeId={connectedForm.values.id!}
        stockTakeLineCount={connectedForm.values.stockTakeLines?.length}
      />
      <PostStockTakeModal
        isOpen={postModalOpen}
        onConfirm={handlePostConfirm}
        onCancel={() => setPostModalOpen(false)}
        disabled={postButtonDisabled}
      />
      <BreadCrumbs
        title="Stock Take"
        customBreadcrumb={[
          { path: '/menu/Products', title: 'Products' },
          { path: '/menu/Products/StockControl', title: 'Stock Control' },
        ]}
      />
      <HeaderContainer>
        <HeaderTitle
          title={'Stock Control'}
          value={`(Legacy) Stock Take ${enumToTitleCase(stockTakeType)}`}
          entityHdrStyle="small"
          status={
            <>
              {stockTakeType === 'PROGRESSIVE' ? (
                <HeaderStatus
                  key={`activationStatus`}
                  icon={active ? undefined : 'error_outline'}
                  statusStyle={active ? StatusStyle.Info : StatusStyle.Danger}
                  title={active ? 'Active' : 'Inactive'}
                />
              ) : null}
              <HeaderStatus
                key={`lineInfo`}
                statusStyle={StatusStyle.Info}
                title={`${stockTake.stockTakeLines?.length ?? 0} Lines`}
              />
            </>
          }
        />
        <HeaderAction>
          <Button data-testid={`form-cancel`} onClick={onCancel} tabIndex={2}>
            Cancel
          </Button>
          <ActionButtons actionButtons={actions} entity={StockTake} />
        </HeaderAction>
      </HeaderContainer>
    </>
  );
};

export default StockTakeHeader;
